import { useCallback, useEffect, useState } from 'react'
import { adminAPI } from '../services/api'
import Spinner from './Spinner'
import { apiErrorMessage } from '../utils/apiError'
import { stopTooTightLabel, useEngineRules } from '../hooks/useEngineRules'
import type { RateInterval } from '../types/adminDiagnostics'
import type { OutcomeStats, ShadowOutcomesReport } from '../types/shadowOutcomes'

/**
 * Admin diagnostics: setups the engine rejected after it had a direction and
 * trade levels, and what they would have done under the live exit rule.
 *
 * Rendered as the server sends it. The live-signal row is computed by the
 * hourly resolver and only read here, so loading this panel never calls the
 * exchange. Never part of the track record.
 */

const GATE_LABELS: Record<string, string> = {
  phase2: 'Phase 2 (ML)',
  phase3: 'Phase 3 (ensemble)',
  short_confidence_floor: 'Short confidence floor',
  stop_too_wide: 'Stop too wide',
  net_ev: 'Net EV after costs',
  htf_disagree: 'Higher timeframe disagrees',
  btc_downtrend: 'BTC below its 50-day EMA (longs)',
  extreme_greed: 'Fear & Greed above the ceiling (longs)',
  short_paused: 'Shorts paused (not published)',
  direction_cap: 'Direction cap (not published)',
  stopout_cooldown: 'Cooldown after a stop-out (not published)',
  engine_paused: 'Engine paused (not published)',
}

const pct = (v: number | null) => (v === null ? '—' : `${(v * 100).toFixed(1)}%`)

const signedR = (v: number | null) => (v === null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}R`)

const interval = (i: RateInterval, fmt: (v: number | null) => string) =>
  i.measured ? `${fmt(i.low)} to ${fmt(i.high)}` : 'too few to bound'

const StatCells = ({ s }: { s: OutcomeStats }) => (
  <>
    <td className="px-2 py-2 text-right tabular-nums">
      {pct(s.gross_win_rate.point)}
      <div className="text-xs text-gray-500">{interval(s.gross_win_rate, pct)}</div>
    </td>
    <td className="px-2 py-2 text-right tabular-nums">
      {signedR(s.avg_net_r.point)}
      <div className="text-xs text-gray-500">{interval(s.avg_net_r, signedR)}</div>
    </td>
    <td className="px-2 py-2 text-right tabular-nums">
      {s.exits.target} / {s.exits.stop} / {s.exits.expired}
    </td>
    <td className="px-2 py-2 text-xs text-amber-700">{s.small_sample ? 'small sample' : ''}</td>
  </>
)

const ShadowOutcomesPanel = () => {
  const [days, setDays] = useState<7 | 30>(7)
  const [report, setReport] = useState<ShadowOutcomesReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const engineRules = useEngineRules()
  const gateLabel = (gate: string) =>
    gate === 'cost_floor' ? stopTooTightLabel(engineRules) : GATE_LABELS[gate] ?? gate

  const load = useCallback(async (window: 7 | 30) => {
    setLoading(true)
    setError(null)
    try {
      setReport(await adminAPI.shadowOutcomes(window))
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load rejected-setup outcomes.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(days)
  }, [days, load])

  const live = report?.live

  return (
    <section className="rounded-lg border border-gray-200 bg-white" aria-label="Rejected setups">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 p-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Rejected setups: what happened</h2>
          <p className="text-sm text-gray-500">
            Setups a gate turned down after they had a direction and levels, resolved on 1h candles under the live
            exit rule. Win rate is gross with a Wilson 95% interval; R is net of fees with a t 95% interval. Exits
            are target / stop / expired. Diagnostics only: never part of the track record.
          </p>
        </div>
        <div className="flex gap-1" role="group" aria-label="Shadow outcome window">
          {([7, 30] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              aria-pressed={days === d}
              className={`rounded-md px-3 py-1 text-sm ${
                days === d ? 'bg-accent text-terminal-bg font-semibold' : 'border border-gray-300 text-gray-700'
              }`}
            >
              {d === 7 ? 'Last 7 days' : 'Last 30 days'}
            </button>
          ))}
        </div>
      </div>

      {loading && !report ? (
        <div className="p-4">
          <Spinner />
        </div>
      ) : error ? (
        <p className="p-4 text-sm text-red-700" role="alert">
          {error}
        </p>
      ) : report ? (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2">Gate</th>
                  <th className="px-2 py-2 text-right">Resolved</th>
                  <th className="px-2 py-2 text-right">Open</th>
                  <th className="px-2 py-2 text-right">Unresolvable</th>
                  <th className="px-2 py-2 text-right">Gross win rate</th>
                  <th className="px-2 py-2 text-right">Avg net R</th>
                  <th className="px-2 py-2 text-right">T / S / E</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {report.gates.map((g) => (
                  <tr key={g.gate} className="text-gray-700" data-testid={`shadow-gate-${g.gate}`}>
                    <td className="px-4 py-2 font-medium text-gray-900">{gateLabel(g.gate)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{g.resolved}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{g.open}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{g.unresolvable}</td>
                    <StatCells s={g} />
                  </tr>
                ))}
                {live?.row && (
                  <>
                    <tr className="bg-gray-50 text-gray-700" data-testid="shadow-live-replayed">
                      <td className="px-4 py-2 font-medium text-gray-900">
                        {live.row.label}
                        {/* The window is by publish date (created_at), not close date. */}
                        <div className="text-xs font-normal text-gray-500" data-testid="shadow-live-window">
                          published in the last {report.days} days
                        </div>
                        <div className="text-xs font-normal text-gray-500">{live.row.replayed.label}</div>
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">{live.row.replayed.n}</td>
                      <td className="px-2 py-2 text-right tabular-nums" colSpan={2}>
                        {live.row.replayed.not_replayed > 0 ? `${live.row.replayed.not_replayed} not replayed` : ''}
                      </td>
                      <StatCells s={live.row.replayed} />
                    </tr>
                    <tr className="bg-gray-50 text-gray-700" data-testid="shadow-live-recorded">
                      <td className="px-4 py-2 font-medium text-gray-900">
                        {live.row.label}
                        <div className="text-xs font-normal text-gray-500">{live.row.recorded.label}</div>
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">{live.row.recorded.n}</td>
                      <td className="px-2 py-2" colSpan={2} />
                      <StatCells s={live.row.recorded} />
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
          <p className="px-4 pt-2 text-xs text-gray-500" data-testid="shadow-live-status">
            {live?.status === 'computed' && live.computed_at
              ? `Live signals replay computed ${new Date(live.computed_at).toLocaleString()}. Live rows count signals published in the window; the engine's win-rate warning counts closes by close date, so the two can differ.`
              : live?.status === 'unavailable'
                ? 'Live signals replay unavailable: Redis could not be read.'
                : 'Live signals replay not computed yet: the hourly resolver has not run.'}
          </p>
          <ul className="list-disc space-y-1 px-8 pb-4 pt-2 text-xs text-gray-500">
            {report.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  )
}

export default ShadowOutcomesPanel
